import { memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CircleIcon from "@mui/icons-material/Circle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReplyIcon from "@mui/icons-material/Reply";
import PersonIcon from "@mui/icons-material/Person";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import { DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { KanbanInstancia, InstanciaTarefa, getStatusConfig, calcularDiasPrazo, corPrazo } from "./kanbanTypes";
import { TempoBadge } from "./TempoBadge";

type Props = {
    inst: KanbanInstancia;
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
    participante: boolean;
    idxModuloAtual: number;
    moduloIdxMap: Map<number, number>;
    userId: number | undefined;
    cardCollapsed: boolean;
    sectionsCollapsed: Record<string, boolean>;
    podeFinalizar: boolean;
    estaNoPrimeiroModulo: boolean;
    onToggleCard: (key: string) => void;
    onToggleSection: (key: string) => void;
    onOpenStatusMenu: (e: React.MouseEvent<HTMLElement>, tarefa: InstanciaTarefa) => void;
    onEditar: (inst: KanbanInstancia) => void;
    onExcluir: (inst: KanbanInstancia) => void;
    onFinalizar: (id: number) => void;
};

function CardInstanciaImpl({
    inst, provided, snapshot, participante, idxModuloAtual, moduloIdxMap, userId,
    cardCollapsed, sectionsCollapsed, podeFinalizar, estaNoPrimeiroModulo,
    onToggleCard, onToggleSection, onOpenStatusMenu, onEditar, onExcluir, onFinalizar,
}: Props) {
    const bloqueadoPorResponsavel = !!(inst.id_usuario_responsavel && inst.id_usuario_responsavel !== userId);

    const grupos = useMemo(() => {
        if (cardCollapsed) return [];
        const map: Record<string, InstanciaTarefa[]> = {};
        for (const t of inst.tarefas) {
            const key = t.nome_modulo || "Geral";
            if (!map[key]) map[key] = [];
            map[key].push(t);
        }
        return Object.entries(map);
    }, [inst.tarefas, cardCollapsed]);

    const finalizadasCount = useMemo(
        () => inst.tarefas.filter(t => t.status === "finalizado").length,
        [inst.tarefas]
    );

    const diasPrazo = calcularDiasPrazo(inst);
    const prazoCor = diasPrazo !== null ? corPrazo(diasPrazo) : null;

    return (
        <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={{
                mb: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: "#fff",
                boxShadow: snapshot.isDragging ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
                cursor: participante ? "grab" : "default",
            }}
        >
            <Box
                onClick={() => onToggleCard(`card-${inst.id_kanban_instancia}`)}
                sx={{ bgcolor: "#f5f5f5", px: 1.5, py: 0.8, borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", cursor: "pointer", "&:hover": { bgcolor: "#eeeeee" } }}
            >
                {cardCollapsed
                    ? <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary", mr: 0.5 }} />
                    : <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary", mr: 0.5 }} />
                }
                <Box sx={{ flex: 1 }}>
                    <Typography fontSize={13} fontWeight={600}>
                        {inst.nome_instancia}
                    </Typography>
                    {inst.nome_grupo && (
                        <Typography fontSize={11} color="text.secondary">
                            {inst.nome_grupo}
                        </Typography>
                    )}
                    {inst.nome_responsavel && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mt: 0.2 }}>
                            <PersonIcon sx={{ fontSize: 12, color: "#1565c0" }} />
                            <Typography fontSize={10} color="#1565c0" fontWeight={500}>
                                {inst.nome_responsavel}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Typography fontSize={10} color="text.secondary" sx={{ mr: 0.5 }}>
                    {finalizadasCount}/{inst.tarefas.length}
                </Typography>
                {diasPrazo !== null && prazoCor && (
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 0.8,
                            py: 0.2,
                            mr: 0.5,
                            borderRadius: 1,
                            bgcolor: prazoCor.bg,
                            border: `1px solid ${prazoCor.border}`,
                        }}
                    >
                        <Typography fontSize={10} fontWeight={700} color={prazoCor.text} noWrap>
                            Prazo: {diasPrazo} {Math.abs(diasPrazo) === 1 ? "dia" : "dias"}
                        </Typography>
                    </Box>
                )}
                {estaNoPrimeiroModulo && (
                    <>
                        <Tooltip title="Editar" arrow>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onEditar(inst); }}
                                sx={{ p: 0.3, color: "#1565c0" }}
                            >
                                <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir" arrow>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onExcluir(inst); }}
                                sx={{ p: 0.3, color: "#d32f2f" }}
                            >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                {podeFinalizar && (
                    <Tooltip title="Finalizar" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onFinalizar(inst.id_kanban_instancia); }}
                            sx={{ p: 0.3, color: "#2e7d32" }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {inst.justificativa_retorno && (
                <Box sx={{ mx: 1, mt: 0.5, p: 1, bgcolor: "#fff3e0", border: "1px solid #ffcc02", borderRadius: 1, display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <ReplyIcon sx={{ fontSize: 16, color: "#e65100", mt: 0.2 }} />
                    <Box>
                        <Typography fontSize={11} fontWeight={600} color="#e65100">
                            Retornado
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">
                            {inst.justificativa_retorno}
                        </Typography>
                    </Box>
                </Box>
            )}
            <Collapse in={!cardCollapsed} mountOnEnter unmountOnExit>
                <Box sx={{ py: 0.5 }}>
                    {grupos.map(([nomeModulo, tarefas], gi) => {
                        const sectionKey = `${inst.id_kanban_instancia}-${nomeModulo}`;
                        const isCollapsed = sectionsCollapsed[sectionKey] ?? true;
                        const finalizadasSec = tarefas.filter(t => t.status === "finalizado").length;
                        return (
                            <Box key={nomeModulo}>
                                {gi > 0 && <Divider sx={{ my: 0.5 }} />}
                                <Box
                                    onClick={() => onToggleSection(sectionKey)}
                                    sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, pt: gi > 0 ? 0.5 : 0, pb: 0.3, cursor: "pointer", "&:hover": { bgcolor: "#f0f7ff" }, borderRadius: 0.5 }}
                                >
                                    {isCollapsed
                                        ? <ExpandMoreIcon sx={{ fontSize: 16, color: "#009BE5" }} />
                                        : <ExpandLessIcon sx={{ fontSize: 16, color: "#009BE5" }} />
                                    }
                                    <ViewKanbanIcon sx={{ fontSize: 13, color: "#009BE5" }} />
                                    <Typography fontSize={11} fontWeight={600} color="#009BE5" sx={{ flex: 1 }}>
                                        {nomeModulo}
                                    </Typography>
                                    <Typography fontSize={10} color="text.secondary">
                                        {finalizadasSec}/{tarefas.length}
                                    </Typography>
                                </Box>
                                <Collapse in={!isCollapsed} mountOnEnter unmountOnExit>
                                    {tarefas.map(tarefa => {
                                        const cfg = getStatusConfig(tarefa.status);
                                        const idxTarefa = tarefa.id_kanban_modulo !== undefined
                                            ? (moduloIdxMap.get(tarefa.id_kanban_modulo) ?? -1)
                                            : -1;
                                        const podeAlterar = participante && idxTarefa >= 0 && idxTarefa <= idxModuloAtual && !bloqueadoPorResponsavel;
                                        return (
                                            <Box key={tarefa.id_kanban_instancia_tarefa}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        px: 0.5,
                                                        py: 0.3,
                                                        "&:hover": { bgcolor: "#fafafa" },
                                                        borderRadius: 0.5,
                                                    }}
                                                >
                                                    <Tooltip title={podeAlterar ? cfg.label : `${cfg.label} (sem permissão)`} arrow>
                                                        <Box
                                                            component="button"
                                                            onClick={podeAlterar ? (e: React.MouseEvent<HTMLElement>) => onOpenStatusMenu(e, tarefa) : undefined}
                                                            sx={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 0.4,
                                                                border: "1px solid",
                                                                borderColor: cfg.color,
                                                                borderRadius: 1,
                                                                px: 0.8,
                                                                py: 0.2,
                                                                bgcolor: `${cfg.color}14`,
                                                                cursor: podeAlterar ? "pointer" : "default",
                                                                opacity: podeAlterar ? 1 : 0.5,
                                                                minWidth: 0,
                                                                outline: "none",
                                                                "&:hover": podeAlterar ? { bgcolor: `${cfg.color}28` } : {},
                                                            }}
                                                        >
                                                            <CircleIcon sx={{ fontSize: 8, color: cfg.color }} />
                                                            <Typography fontSize={10} fontWeight={600} color={cfg.color} noWrap>
                                                                {cfg.label}
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                    <Typography
                                                        fontSize={12}
                                                        sx={{
                                                            flex: 1,
                                                            ml: 0.8,
                                                            textDecoration: tarefa.status === "finalizado" ? "line-through" : "none",
                                                            color: tarefa.status === "finalizado" ? "text.disabled" : "text.primary",
                                                        }}
                                                    >
                                                        {tarefa.nome_tarefa}
                                                    </Typography>
                                                    <TempoBadge tarefa={tarefa} />
                                                </Box>
                                                {tarefa.justificativa_atraso && (
                                                    <Box sx={{ mx: 0.5, mt: 0.3, mb: 0.5, p: 0.8, bgcolor: "#ffebee", border: "1px solid #ef5350", borderRadius: 1, display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                                        <AccessTimeIcon sx={{ fontSize: 14, color: "#c62828", mt: 0.1 }} />
                                                        <Box>
                                                            <Typography fontSize={10} fontWeight={600} color="#c62828">
                                                                Atraso justificado
                                                            </Typography>
                                                            <Typography fontSize={10} color="text.secondary">
                                                                {tarefa.justificativa_atraso}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Collapse>
                            </Box>
                        );
                    })}
                </Box>
            </Collapse>
        </Box>
    );
}

export const CardInstancia = memo(CardInstanciaImpl);
